import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    IconButton,
    Skeleton,
    ToggleButton,
    ToggleButtonGroup,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useFormik } from 'formik';
import {
    ArrowLeft,
    Building2,
    Car,
    CheckCircle2,
    Eye,
    FileText,
    Pencil,
    Plus,
    ShoppingCart,
    Trash2,
    User,
    X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import { formatCNPJInput, formatDocument, formatPhone, isValidCNPJ } from '../../common/formatters';
import { httpClient } from '../../common/httpclient';
import StatusChip from '../../component/common/StatusChip';
import DialogForm from '../../component/dialog/DialogForm';
import Dropdown from '../../component/dropdown/Dropdown';
import InputMasked from '../../component/input/InputMasked';
import TextAreaWithCounter from '../../component/input/TextAreaWithCounter';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import DefaultTable from '../../component/table/DefaultTable';
import { usePermissions } from '../../hooks/usePermissions';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import MobileCustomerDetail from '../../component/customer/MobileCustomerDetail';

// Lista estática de tipos de atividade (PJ) — cópia local intencional de
// `customers/Form.js` `ACTIVITY_TYPES` (mesma convenção de duplicação já
// usada entre `Operator.js`/`sales/View.js`/`Sale.js`).
const ACTIVITY_TYPES = [
    'Industrial',
    'Comercial (Varejista)',
    'Prestação de Serviço',
    'Construção Civil',
    'Comercial (Atacadista)',
    'Comercial (Exportadora)',
    'Importador (Varejista)',
    'Importador (Atacadista)',
    'Associação',
].map((label) => ({ id: label, name: label }));

// `<span class="ff-req">*</span>` do protótipo — asterisco de campo
// obrigatório na cor de erro, não na cor padrão do label.
const requiredFieldLabel = (text) => (
    <>
        {text} <Box component="span" sx={{ color: 'error.main' }}>*</Box>
    </>
);

// `.hero-chip` — pill translúcido branco sobre o gradiente do `client-hero`.
function HeroChip({ icon: Icon, label }) {
    return (
        <Box sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            fontSize: '11px',
            fontWeight: 700,
            padding: '5px 11px',
            borderRadius: '20px',
            bgcolor: 'rgba(255,255,255,.18)',
            color: '#fff',
        }}>
            <Icon size={12} strokeWidth={2.4} />
            {label}
        </Box>
    );
}

// `.hero-actions button` — ghost translúcido sobre o gradiente, mesmo padrão
// já usado em `sales/View.js`/`service_order/View.js`.
function HeaderActionButton({ icon: Icon, label, onClick }) {
    return (
        <Button
            onClick={onClick}
            startIcon={<Icon size={14} strokeWidth={2} />}
            sx={{
                padding: '9px 14px',
                borderRadius: '10px',
                border: '1px solid rgba(255,255,255,.3)',
                bgcolor: 'rgba(255,255,255,.12)',
                color: '#fff',
                fontSize: '12.5px',
                fontWeight: 700,
                '&:hover': { bgcolor: 'rgba(255,255,255,.22)', borderColor: 'rgba(255,255,255,.3)' },
            }}
        >
            {label}
        </Button>
    );
}

// `.client-meta-grid .mi` — par label/valor do cabeçalho.
function MetaItem({ label, value, loading }) {
    return (
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '10.5px', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '.05em', mb: '4px', color: '#fff' }}>
                {label}
            </Typography>
            {loading ? (
                <Skeleton variant="text" width={90} height={20} sx={{ bgcolor: 'rgba(255,255,255,.3)' }} />
            ) : (
                <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: '#fff' }} noWrap title={value}>
                    {value}
                </Typography>
            )}
        </Box>
    );
}

// `.ro-field` — label uppercase micro + valor, modo leitura dos `field-card`s
// (DESIGN-SYSTEM.md §5.5/`prototipo-configuracoes.html`).
function RoField({ label, value, span }) {
    return (
        <Box sx={{ gridColumn: span ? 'span 2' : 'auto', minWidth: 0 }}>
            <Typography sx={{ fontSize: '10.5px', fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '.05em', mb: '4px' }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: 'text.primary' }}>
                {value || '—'}
            </Typography>
        </Box>
    );
}

// `.field-card` — card com cabeçalho (título + botão Editar) e toggle
// "Editar → Salvar/Cancelar" por card, reaproveitado verbatim de
// `prototipo-configuracoes.html` (DESIGN-SYSTEM.md §6.4).
function FieldCard({ title, editing, canEdit, onEdit, onCancel, onSave, saving, span, children }) {
    return (
        <Card elevation={0} sx={{ gridColumn: span ? { xs: '1 / -1', md: '1 / -1' } : 'auto' }}>
            <CardContent sx={{ p: '18px 20px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.75 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>{title}</Typography>
                    {canEdit && !editing && (
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Pencil size={13} strokeWidth={2} />}
                            onClick={onEdit}
                        >
                            Editar
                        </Button>
                    )}
                </Box>
                {children}
                {editing && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2.25 }}>
                        <Button size="small" variant="outlined" onClick={onCancel} disabled={saving}>
                            Cancelar
                        </Button>
                        <Button size="small" variant="contained" onClick={onSave} disabled={saving}>
                            {saving ? 'Salvando…' : 'Salvar'}
                        </Button>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}

// `.empty-state` — dashed-circle + texto + ação (DESIGN-SYSTEM.md §5.11).
function EmptyMiniState({ icon: Icon, title, subtitle, actionLabel, actionIcon: ActionIcon = Plus, onAction, size = 72 }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 6, px: 3, textAlign: 'center' }}>
            <Box sx={{
                width: size,
                height: size,
                borderRadius: '50%',
                border: '2px dashed',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.disabled',
                mb: 0.5,
            }}>
                <Icon size={26} strokeWidth={1.6} />
            </Box>
            <Typography sx={{ fontSize: '15px', fontWeight: 700, color: 'text.primary' }}>{title}</Typography>
            {subtitle && <Typography sx={{ fontSize: '12.5px', color: 'text.disabled', maxWidth: 340 }}>{subtitle}</Typography>}
            {actionLabel && (
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<ActionIcon size={13} strokeWidth={2} />}
                    onClick={onAction}
                    sx={{ mt: 1.25 }}
                >
                    {actionLabel}
                </Button>
            )}
        </Box>
    );
}

// `.panel-head` — cabeçalho das abas Veículos/Ordens.
function PanelHead({ title, subtitle, action }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, p: '18px 20px 0', flexWrap: 'wrap' }}>
            <Box>
                <Typography sx={{ fontSize: '14.5px', fontWeight: 700, mb: '2px' }}>{title}</Typography>
                <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>{subtitle}</Typography>
            </Box>
            {action}
        </Box>
    );
}

// `.ffs`/`.ff-input` — casca de campo standalone (52px, radius 12px, fundo
// surface-2, label flutuante/estático interno) — cópia 1:1 de
// `products/Form.js`/`customers/Form.js` `standaloneInputBoxSx`/`standaloneFieldSx`.
function buildStandaloneFieldSx() {
    const standaloneInputBoxSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: 'background.subtle',
            height: '52px',
            transition: 'background-color .15s ease, box-shadow .15s ease',
            '&.MuiInputBase-multiline': {
                height: 'auto',
                minHeight: '96px',
                alignItems: 'flex-start',
                padding: '24px 14px 7px 14px',
            },
        },
        '& .MuiOutlinedInput-input:not(textarea):not(.MuiSelect-select)': {
            padding: '21px 14px 7px 14px',
        },
        '& .MuiSelect-select.MuiOutlinedInput-input': {
            padding: '0 14px',
            display: 'flex',
            alignItems: 'center',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1.5px',
            borderColor: 'divider',
        },
        '& .MuiOutlinedInput-notchedOutline legend': {
            display: 'none',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main',
            borderWidth: '1.5px',
        },
        '& .MuiInputBase-multiline textarea': {
            resize: 'vertical',
        },
    };

    return {
        '& .MuiInputBase-input': { fontSize: '13.5px' },
        '& .MuiInputLabel-root': {
            fontSize: '13.5px',
            color: 'text.disabled',
            transform: 'translate(14px, 17px) scale(1)',
        },
        '& .MuiInputLabel-root.MuiInputLabel-shrink': {
            fontSize: '10px',
            fontWeight: 800,
            color: 'primary.main',
            letterSpacing: '.04em',
            textTransform: 'uppercase',
            transform: 'translate(14px, 8px) scale(1)',
        },
        ...standaloneInputBoxSx,
    };
}

const standaloneFieldSx = buildStandaloneFieldSx();

// `TbLabel` — micro-label uppercase fora do campo (DESIGN-SYSTEM.md §5.5
// "Static-label select"), usado só no fallback de loading do Dropdown de
// Fabricante (que já usa `labelVariant="static-micro"` quando não está
// carregando).
function StaticMicroLabel({ children }) {
    return (
        <Typography sx={{ fontSize: '10.5px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
            {children}
        </Typography>
    );
}

const roGridSx = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 16px' };
const editGridSx = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 };

// Schemas de validação para a atualização do cliente (edição inline por
// `field-card`) — cópia 1:1 de `customers/Form.js` `pfValidationSchema`/
// `pjValidationSchema`: como o PUT sempre substitui o registro inteiro
// (`/customers/person|enterprise/{id}`), qualquer card que for salvo valida
// o objeto completo, exatamente como o formulário de edição de página cheia.
const pfValidationSchema = Yup.object({
    first_name: Yup.string().required('Nome é obrigatório'),
    last_name: Yup.string().required('Sobrenome é obrigatório'),
    email: Yup.string().email('E-mail inválido'),
    cell_phone: Yup.string(),
    phone_number: Yup.string(),
    address: Yup.string(),
    complement: Yup.string(),
    number_address: Yup.number(),
    neighborhood: Yup.string(),
    city: Yup.string(),
    cep: Yup.string().matches(/^\d{5}-\d{3}$/, 'CEP inválido').nullable().transform((v) => (v ? v : undefined)),
});

const pjValidationSchema = Yup.object({
    trade_name: Yup.string().required('Nome fantasia é obrigatório'),
    legal_name: Yup.string().required('Razão social é obrigatória'),
    cnpj: Yup.string()
        .required('CNPJ é obrigatório')
        // CNPJ numérico ou alfanumérico (Nota Técnica 2026.001 da Receita
        // Federal) — mesma validação de `customers/Form.js` (`isValidCNPJ`
        // já cobre formato + dígito verificador).
        .test('cnpj-dv', 'CNPJ inválido', (value) => !value || isValidCNPJ(value)),
    state_registration: Yup.string().required('Inscrição estadual é obrigatória'),
    municipal_registration: Yup.string().required('Inscrição municipal é obrigatória'),
    activity_type: Yup.string().required('Tipo de atividade é obrigatório'),
    principal_cnae: Yup.string(),
    email: Yup.string().email('E-mail inválido'),
    cell_phone: Yup.string(),
    phone_number: Yup.string(),
    address: Yup.string(),
    complement: Yup.string(),
    number_address: Yup.number(),
    neighborhood: Yup.string(),
    city: Yup.string(),
    cep: Yup.string().matches(/^\d{5}-\d{3}$/, 'CEP inválido').nullable().transform((v) => (v ? v : undefined)),
});

function buildCustomerInitialValues(c) {
    return {
        first_name: c?.first_name || '',
        last_name: c?.last_name || '',
        cpf: c?.cpf || '',
        birthdate: c?.birthdate || '',
        legal_name: c?.legal_name || '',
        trade_name: c?.trade_name || '',
        cnpj: c?.cnpj || '',
        state_registration: c?.state_registration || '',
        municipal_registration: c?.municipal_registration || '',
        activity_type: c?.activity_type || '',
        principal_cnae: c?.principal_cnae || '',
        email: c?.email || '',
        cell_phone: c?.cell_phone || '',
        phone_number: c?.phone_number || '',
        address: c?.address || '',
        complement: c?.complement || '',
        number_address: c?.number_address ?? '',
        neighborhood: c?.neighborhood || '',
        city: c?.city || '',
        cep: c?.cep || '',
    };
}

function getInitials(name) {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase();
}

const TAB_INDEX_MAP = { 0: 'cadastro', 1: 'veiculos', 2: 'ordens', 3: 'vendas' };

export default function CustomerView() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { hasPermission } = usePermissions();
    const isMobileShell = useIsMobileShell();
    const [openDeleteCustomerDialog, setOpenDeleteCustomerDialog] = useState(false);

    const [customer, setCustomer] = useState(null);
    const [vehicles, setVehicles] = useState([]);
    const [orderServices, setOrderServices] = useState([]);
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('cadastro');

    // Edição inline por `field-card` — DESIGN-SYSTEM.md §6.4.
    const [editingCards, setEditingCards] = useState({ cadastrais: false, contato: false, endereco: false });
    const [savingCustomer, setSavingCustomer] = useState(false);

    // Modal de adicionar/editar veículo (§5.6 center modal).
    const [openVehicleModal, setOpenVehicleModal] = useState(false);
    const [manufacturers, setManufacturers] = useState([]);
    const [loadingManufacturers, setLoadingManufacturers] = useState(false);
    const [selectedType, setSelectedType] = useState('');

    // Confirmação de remoção de veículo.
    const [vehicleToDelete, setVehicleToDelete] = useState(null);
    const [openDeleteVehicleDialog, setOpenDeleteVehicleDialog] = useState(false);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

    useEffect(() => {
        const tabParam = searchParams.get('tabActive');
        if (tabParam !== null && TAB_INDEX_MAP[tabParam]) {
            setActiveTab(TAB_INDEX_MAP[tabParam]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    useEffect(() => {
        fetchCustomerData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchCustomerData = async () => {
        try {
            setLoading(true);
            const response = await httpClient.get(`/customers/${id}`);
            // `GET /customers/{id}` responde 200 com corpo `null` (em vez de
            // 404) quando o id não existe/não pertence à empresa atual — sem
            // essa checagem a página ficava com o hero "quebrado" (avatar
            // "?", tudo "—", nenhuma aba), em vez de uma mensagem de erro
            // clara.
            if (!response.data || !response.data.id) {
                setError('Cliente não encontrado');
                return;
            }
            setCustomer(response.data);

            try {
                const vehiclesResponse = await httpClient.get(`/vehicles/customer/${id}`);
                setVehicles(vehiclesResponse.data || []);
            } catch (vehiclesErr) {
                console.warn('Could not fetch vehicles:', vehiclesErr);
                setVehicles([]);
            }

            try {
                const orderServicesResponse = await httpClient.get(`/orderservices/customer/${id}`);
                setOrderServices(orderServicesResponse.data || []);
            } catch (orderServicesErr) {
                console.warn('Could not fetch order services:', orderServicesErr);
                setOrderServices([]);
            }

            try {
                const salesResponse = await httpClient.get(`/sales/customer/${id}`);
                setSales(salesResponse.data || []);
            } catch (salesErr) {
                console.warn('Could not fetch sales:', salesErr);
                setSales([]);
            }
        } catch (err) {
            setError('Erro ao carregar dados do cliente');
            console.error('Error fetching customer data:', err);
        } finally {
            setLoading(false);
        }
    };

    // Mobile: "Excluir cliente" na folha de ações (mesmo DELETE /customers/{id} da lista).
    const handleDeleteCustomer = async () => {
        try {
            const response = await httpClient.delete(`/customers/${id}`);
            if (response.status === 204) {
                navigate('/customers', { replace: true });
                return;
            }
            showSnackbar('Erro ao excluir cliente', 'error');
        } catch (err) {
            console.error('Error deleting customer:', err);
            showSnackbar('Erro ao excluir cliente', 'error');
        } finally {
            setOpenDeleteCustomerDialog(false);
        }
    };

    // Mobile: aba inicial vem de ?tabActive (0 Dados, 1 Veículos, 2/3 Histórico); sem parâmetro abre Histórico.
    const mobileInitialTab = { 0: 'd', 1: 'v', 2: 'h', 3: 'h' }[searchParams.get('tabActive')] || 'h';
    const handleMobileTabChange = (value) => {
        setSearchParams({ tabActive: { d: '0', v: '1', h: '2' }[value] }, { replace: true });
    };

    const handleViewVehicle = (vehicleId) => {
        navigate(`/vehicles/edit/${vehicleId}?location=customers/view/${id}&tabActive=1`);
    };

    const handleViewOrderService = (orderServiceId) => {
        navigate(`/serviceorders/${orderServiceId}/view?location=customers/view/${id}&tabActive=2`);
    };

    // ==================== CLIENTE (edição inline por field-card) ====================

    const personType = customer?.customer_type === 'pj' ? 'pj' : 'pf';

    const customerFormik = useFormik({
        enableReinitialize: true,
        initialValues: buildCustomerInitialValues(customer),
        validationSchema: personType === 'pj' ? pjValidationSchema : pfValidationSchema,
        onSubmit: (values) => handleUpdateCustomer(values),
    });

    // Máscara manual do CNPJ (aceita letra OU dígito nos 12 primeiros
    // caracteres — CNPJ alfanumérico, Nota Técnica 2026.001 da Receita
    // Federal) — mesma técnica de `customers/Form.js` (`handleCnpjChange`).
    const handleCnpjChange = (event) => {
        customerFormik.setFieldValue('cnpj', formatCNPJInput(event.target.value));
    };

    const sectionFields = {
        cadastrais: personType === 'pj'
            ? ['legal_name', 'trade_name', 'cnpj', 'state_registration', 'municipal_registration', 'activity_type', 'principal_cnae']
            : ['first_name', 'last_name', 'cpf', 'birthdate'],
        contato: ['email', 'cell_phone', 'phone_number'],
        endereco: ['address', 'complement', 'number_address', 'neighborhood', 'city', 'cep'],
    };

    const handleEditSection = (section) => setEditingCards((prev) => ({ ...prev, [section]: true }));

    const handleCancelSection = (section) => {
        const initialValues = buildCustomerInitialValues(customer);
        sectionFields[section].forEach((field) => customerFormik.setFieldValue(field, initialValues[field], false));
        setEditingCards((prev) => ({ ...prev, [section]: false }));
    };

    const handleSaveSection = () => {
        customerFormik.handleSubmit();
    };

    const handleUpdateCustomer = async (values) => {
        try {
            setSavingCustomer(true);
            const endpoint = `/customers/${personType === 'pf' ? 'person' : 'enterprise'}/${id}`;

            const payload = personType === 'pf' ? {
                first_name: values.first_name,
                last_name: values.last_name,
                email: values.email,
                cpf: values.cpf,
                birthdate: values.birthdate,
                cell_phone: values.cell_phone,
                phone_number: values.phone_number,
                address: values.address,
                complement: values.complement,
                number_address: parseInt(values.number_address),
                neighborhood: values.neighborhood,
                city: values.city,
                cep: values.cep,
                customer_type: 'pf',
            } : {
                trade_name: values.trade_name,
                legal_name: values.legal_name,
                cnpj: values.cnpj,
                state_registration: values.state_registration,
                municipal_registration: values.municipal_registration,
                activity_type: values.activity_type,
                principal_cnae: values.principal_cnae,
                email: values.email,
                cell_phone: values.cell_phone,
                phone_number: values.phone_number,
                address: values.address,
                complement: values.complement,
                number_address: parseInt(values.number_address),
                neighborhood: values.neighborhood,
                city: values.city,
                cep: values.cep,
                customer_type: 'pj',
            };

            const response = await httpClient.put(endpoint, payload);
            if (response.status === 204) {
                setCustomer((prev) => ({ ...prev, ...payload }));
                setEditingCards({ cadastrais: false, contato: false, endereco: false });
                showSnackbar('Dados do cliente atualizados com sucesso!', 'success');
            } else {
                showSnackbar('Erro ao atualizar dados do cliente', 'error');
            }
        } catch (error) {
            console.error('Error updating customer:', error);
            showSnackbar('Erro ao atualizar dados do cliente', 'error');
        } finally {
            setSavingCustomer(false);
        }
    };

    // ==================== VEÍCULO: modal adicionar ====================

    const fetchManufacturers = async (type) => {
        try {
            setLoadingManufacturers(true);
            const response = await httpClient.get(`/vehicles/manufacturers/${type}`);
            if (Array.isArray(response.data)) {
                const validManufacturers = response.data.filter((item) =>
                    item && typeof item === 'object' && item.id && item.name
                );
                setManufacturers(validManufacturers);
            } else {
                setManufacturers([]);
            }
        } catch (error) {
            console.error('Error fetching manufacturers:', error);
            setManufacturers([]);
        } finally {
            setLoadingManufacturers(false);
        }
    };

    const handleOpenVehicleModal = () => setOpenVehicleModal(true);

    const handleCloseVehicleModal = () => {
        setOpenVehicleModal(false);
        vehicleFormik.resetForm();
        setManufacturers([]);
        setSelectedType('');
    };

    const handleCreateVehicle = async (values) => {
        try {
            const vehicleData = { ...values, customer_id: id };
            const response = await httpClient.post('/vehicles', vehicleData);
            if (response.status === 201 || response.status === 200) {
                showSnackbar('Veículo cadastrado com sucesso!', 'success');
                await fetchCustomerData();
                handleCloseVehicleModal();
            } else {
                showSnackbar(response.data?.message || response.data?.id || 'Erro ao criar veículo', 'error');
            }
        } catch (error) {
            console.error('Error creating vehicle:', error);
            const errorMessage = error.response?.data || error.message || 'Erro ao criar veículo';
            showSnackbar(typeof errorMessage === 'string' ? errorMessage : 'Erro ao criar veículo', 'error');
        }
    };

    const handleChangeType = (value) => {
        vehicleFormik.setFieldValue('type', value);
        vehicleFormik.setFieldValue('manufacturer_id', '');
        setManufacturers([]);
        setLoadingManufacturers(false);
        setSelectedType(value);
    };

    const handleChangeManufacturer = (value) => {
        vehicleFormik.setFieldValue('manufacturer_id', value);
    };

    const handleLicensePlateChange = (event) => {
        const value = event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
        if (value.length > 3) {
            vehicleFormik.setFieldValue('license_plate', value.slice(0, 3) + '-' + value.slice(3));
        } else {
            vehicleFormik.setFieldValue('license_plate', value);
        }
    };

    const vehicleValidationSchema = Yup.object({
        type: Yup.string().required('Tipo é obrigatório'),
        manufacturer_id: Yup.string().required('Fabricante é obrigatório'),
        model: Yup.string().required('Modelo é obrigatório'),
        fab_year: Yup.number()
            .required('Ano de fabricação é obrigatório')
            .min(1900, 'Ano de fabricação inválido')
            .max(new Date().getFullYear() + 1, 'Ano de fabricação inválido'),
        mod_year: Yup.number()
            .required('Ano do modelo é obrigatório')
            .min(1900, 'Ano do modelo inválido')
            .max(new Date().getFullYear() + 1, 'Ano do modelo inválido'),
        license_plate: Yup.string()
            .when('type', {
                is: (type) => type !== 'bicycle',
                then: (schema) => schema.required('Placa é obrigatória'),
                otherwise: (schema) => schema.notRequired()
            }),
        chassi: Yup.string()
            .when('type', {
                is: (type) => type !== 'bicycle',
                then: (schema) => schema.required('Chassi é obrigatório').length(17, 'Chassi deve ter 17 caracteres'),
                otherwise: (schema) => schema.required('SN (Serial Number) é obrigatório')
            }),
        color: Yup.string().required('Cor é obrigatória'),
        kilometer: Yup.number()
            .min(0, 'Quilometragem deve ser maior ou igual a 0'),
        observation: Yup.string().max(500, 'Observação deve ter no máximo 500 caracteres')
    });

    const vehicleFormik = useFormik({
        initialValues: {
            type: '',
            manufacturer_id: '',
            model: '',
            fab_year: '',
            mod_year: '',
            license_plate: '',
            chassi: '',
            color: '',
            kilometer: '',
            observation: ''
        },
        validationSchema: vehicleValidationSchema,
        onSubmit: (values) => {
            handleCreateVehicle(values);
        },
    });

    useEffect(() => {
        if (vehicleFormik.values.type && !loadingManufacturers) {
            fetchManufacturers(vehicleFormik.values.type);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicleFormik.values.type]);

    // ==================== VEÍCULO: remoção ====================

    const handleOpenDeleteVehicle = (row) => {
        setVehicleToDelete(row);
        setOpenDeleteVehicleDialog(true);
    };

    const handleCloseDeleteVehicle = () => {
        setOpenDeleteVehicleDialog(false);
        setVehicleToDelete(null);
    };

    const handleConfirmDeleteVehicle = async () => {
        if (!vehicleToDelete) return;
        try {
            const response = await httpClient.delete(`/vehicles/${vehicleToDelete.ID}`);
            if (response.status === 204) {
                showSnackbar('Veículo removido com sucesso!', 'success');
                await fetchCustomerData();
            } else {
                showSnackbar('Erro ao remover veículo', 'error');
            }
        } catch (error) {
            console.error('Error deleting vehicle:', error);
            showSnackbar('Erro ao remover veículo', 'error');
        } finally {
            handleCloseDeleteVehicle();
        }
    };

    // ==================== Tabelas ====================

    const vehiclesColumns = [
        {
            path: 'LicensePlate',
            name: 'Placa',
            invisible: false,
            render: (row) => (
                <Box>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>
                        {row.LicensePlate || '—'}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: '1px' }}>
                        {row.Chassi || '—'}
                    </Typography>
                </Box>
            ),
        },
        {
            path: 'Model',
            name: 'Veículo',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                    {[row.Manufacturer, row.Model].filter(Boolean).join(' ') || '—'}
                </Typography>
            ),
        },
        {
            path: 'ModYear',
            name: 'Ano',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                    {row.FabYear && row.ModYear
                        ? (row.FabYear === row.ModYear ? row.FabYear : `${row.FabYear}/${row.ModYear}`)
                        : (row.FabYear || row.ModYear || '—')}
                </Typography>
            ),
        },
        {
            path: 'Color',
            name: 'Cor',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{row.Color || '—'}</Typography>
            ),
        },
        {
            path: 'Kilometer',
            name: 'Km atual',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                    {row.Kilometer !== undefined && row.Kilometer !== null ? `${row.Kilometer} km` : '—'}
                </Typography>
            ),
        },
    ];

    const vehicleActions = [
        ...(hasPermission('vehicle.update') ? [{
            icon: <Pencil size={13} strokeWidth={2} />,
            props: {
                onClick: (row) => handleViewVehicle(row.ID),
                tooltip: 'Editar',
                style: { cursor: 'pointer' },
            },
        }] : []),
        ...(hasPermission('vehicle.delete') ? [{
            icon: <Trash2 size={13} strokeWidth={2} color={theme.palette.error.main} />,
            props: {
                onClick: (row) => handleOpenDeleteVehicle(row),
                tooltip: 'Remover',
                style: { cursor: 'pointer' },
            },
        }] : []),
    ];

    const orderServicesColumns = [
        {
            path: 'number',
            name: 'Número',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>{row.number}</Typography>
            ),
        },
        {
            path: 'created_at',
            name: 'Data',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                    {new Date(row.created_at).toLocaleString('pt-BR', {
                        timeZone: 'UTC',
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    })}
                </Typography>
            ),
        },
        {
            path: 'status',
            name: 'Status',
            invisible: false,
            render: (row) => <StatusChip domain="orderService" value={row.status} />,
        },
        {
            path: 'amount',
            name: 'Valor',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary' }}>
                    R$ {row.amount}
                </Typography>
            ),
        },
    ];

    const handleViewSale = (saleId) => {
        navigate(`/sales/${saleId}`);
    };

    const salesColumns = [
        {
            path: 'number',
            name: 'Número',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>{row.number}</Typography>
            ),
        },
        {
            path: 'created_date',
            name: 'Data',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{row.created_date}</Typography>
            ),
        },
        {
            path: 'seller',
            name: 'Vendedor',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{row.seller || '—'}</Typography>
            ),
        },
        {
            path: 'status',
            name: 'Status',
            invisible: false,
            render: (row) => <StatusChip domain="sale" value={row.status} />,
        },
        {
            path: 'final_total',
            name: 'Valor',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary' }}>
                    R$ {row.final_total}
                </Typography>
            ),
        },
    ];

    const saleActions = [
        {
            icon: <Eye size={13} strokeWidth={2} />,
            props: {
                onClick: (row) => handleViewSale(row.id),
                tooltip: 'Visualizar',
                style: { cursor: 'pointer' },
            },
        },
    ];

    const orderServiceActions = [
        {
            icon: <Eye size={13} strokeWidth={2} />,
            props: {
                onClick: (row) => handleViewOrderService(row.order_service_id),
                tooltip: 'Visualizar',
                style: { cursor: 'pointer' },
            },
        },
    ];

    const breadcrumbs = [{ title: 'Clientes', link: '/customers' }];

    if (error) {
        return (
            <PageScaffold titlePageTarget="Visualizar Cliente" links={breadcrumbs}>
                <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto' }}>
                    <Card elevation={0}>
                        <CardContent>
                            <EmptyMiniState
                                icon={User}
                                title={error}
                                subtitle="Verifique se o link está correto ou volte para a lista de clientes."
                                actionLabel="Voltar para clientes"
                                actionIcon={ArrowLeft}
                                onAction={() => navigate('/customers')}
                            />
                        </CardContent>
                    </Card>
                </Box>
            </PageScaffold>
        );
    }

    const displayName = customer
        ? (personType === 'pf' ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim() : (customer.legal_name || ''))
        : '';
    const docLabel = personType === 'pf' ? 'CPF' : 'CNPJ';
    const docValue = personType === 'pf' ? customer?.cpf : customer?.cnpj;

    return (
        <PageScaffold
            titlePageTarget="Visualizar Cliente"
            links={breadcrumbs}
            openSnackbar={snackbar.open}
            setOpenSnackbar={(open) => setSnackbar((s) => ({ ...s, open }))}
            message={snackbar.message}
            severity={snackbar.severity}
        >
            {isMobileShell ? (
                <MobileCustomerDetail
                    loading={loading}
                    customer={customer}
                    personType={personType}
                    displayName={displayName}
                    docLabel={docLabel}
                    docValue={docValue}
                    vehicles={vehicles}
                    orderServices={orderServices}
                    sales={sales}
                    initialTab={mobileInitialTab}
                    onTabChange={handleMobileTabChange}
                    canUpdate={hasPermission('customer.update')}
                    canDelete={hasPermission('customer.delete')}
                    canCreateOrder={hasPermission('serviceorder.create')}
                    canCreateQuote={hasPermission('servicequote.create')}
                    canCreateVehicle={hasPermission('vehicle.create')}
                    canDeleteVehicle={hasPermission('vehicle.delete')}
                    onEdit={() => navigate(`/customers/edit/${id}`)}
                    onDelete={() => setOpenDeleteCustomerDialog(true)}
                    onGenerateOrder={() => navigate('/serviceorder/generate', { state: { prefill: { customerId: id } } })}
                    onGenerateQuote={() => navigate('/servicequotes/generate', { state: { prefill: { customerId: id } } })}
                    onOpenVehicle={(vehicleId) => (hasPermission('vehicle.update') ? handleViewVehicle(vehicleId) : undefined)}
                    onDeleteVehicle={handleOpenDeleteVehicle}
                    onAddVehicle={handleOpenVehicleModal}
                    onOpenOrder={handleViewOrderService}
                    onOpenSale={handleViewSale}
                />
            ) : (
            <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto' }}>
                {/* HERO — client-hero (gradiente de marca, §2.4/§5.2) */}
                <Card elevation={0} sx={{
                    borderRadius: '20px',
                    mb: 2.25,
                    padding: '22px 24px',
                    background: theme.palette.gradient.brand,
                    boxShadow: theme.customShadows.md,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, flexWrap: 'wrap' }}>
                            <Tooltip title="Voltar">
                                <IconButton
                                    onClick={() => navigate(-1)}
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: '10px',
                                        bgcolor: 'rgba(255,255,255,.15)',
                                        border: '1px solid rgba(255,255,255,.25)',
                                        color: '#fff',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,.25)' },
                                    }}
                                >
                                    <ArrowLeft size={16} strokeWidth={2} />
                                </IconButton>
                            </Tooltip>
                            <Box sx={{
                                width: 52,
                                height: 52,
                                borderRadius: '16px',
                                bgcolor: 'rgba(255,255,255,.18)',
                                border: '2px solid rgba(255,255,255,.35)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '18px',
                                fontWeight: 800,
                                color: '#fff',
                                flexShrink: 0,
                            }}>
                                {loading ? <Skeleton variant="circular" width={22} height={22} sx={{ bgcolor: 'rgba(255,255,255,.3)' }} /> : getInitials(displayName)}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                                {loading ? (
                                    <Skeleton variant="text" width={200} height={30} sx={{ bgcolor: 'rgba(255,255,255,.3)' }} />
                                ) : (
                                    <Typography component="h1" sx={{ fontSize: '20px', fontWeight: 800, letterSpacing: '-.01em', color: '#fff', mb: '8px' }}>
                                        {displayName || '—'}
                                    </Typography>
                                )}
                                {!loading && customer && (
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <HeroChip icon={personType === 'pf' ? User : Building2} label={personType === 'pf' ? 'Pessoa Física' : 'Pessoa Jurídica'} />
                                        {/* Não existe conceito de cliente inativo hoje (exclusão é física,
                                            `DELETE /customers/{id}`) — todo cliente carregável está, por
                                            definição, ativo; chip constante, sem depender de um campo novo
                                            no backend. */}
                                        <HeroChip icon={CheckCircle2} label="Ativo" />
                                        <HeroChip icon={FileText} label={`${docLabel} ${formatDocument(docValue) || '—'}`} />
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {!loading && customer && hasPermission('customer.update') && (
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <HeaderActionButton icon={Pencil} label="Editar cliente" onClick={() => navigate(`/customers/edit/${id}`)} />
                            </Box>
                        )}
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 2.25 }}>
                        <MetaItem label="Telefone" value={formatPhone(customer?.phone_number) || '—'} loading={loading} />
                        <MetaItem label="Celular / WhatsApp" value={formatPhone(customer?.cell_phone) || '—'} loading={loading} />
                        <MetaItem label="E-mail" value={customer?.email || '—'} loading={loading} />
                        <MetaItem label="Cidade" value={customer?.city || '—'} loading={loading} />
                    </Box>
                </Card>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress size={32} />
                    </Box>
                ) : customer && (
                    <>
                        {/* SEG-TABS — Dados Cadastrais / Veículos / Ordens (§5.4) */}
                        <ToggleButtonGroup
                            exclusive
                            value={activeTab}
                            onChange={(e, v) => {
                                if (!v) return;
                                setActiveTab(v);
                                const tabIndex = Object.keys(TAB_INDEX_MAP).find((k) => TAB_INDEX_MAP[k] === v);
                                setSearchParams({ tabActive: tabIndex }, { replace: true });
                            }}
                            sx={{ mb: 2.25, width: 'fit-content' }}
                        >
                            <ToggleButton value="cadastro" sx={{ gap: 0.75, px: 2.25 }}>
                                <User size={14} strokeWidth={2} /> Dados Cadastrais
                            </ToggleButton>
                            <ToggleButton value="veiculos" sx={{ gap: 0.75, px: 2.25 }}>
                                <Car size={14} strokeWidth={2} /> Veículos
                                <Box component="span" sx={{
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    minWidth: 18, height: 18, px: '5px', borderRadius: '9px', fontSize: '10px', fontWeight: 800,
                                    bgcolor: activeTab === 'veiculos' ? 'info.bg' : 'background.default', color: activeTab === 'veiculos' ? 'primary.main' : 'text.secondary',
                                }}>
                                    {vehicles.length}
                                </Box>
                            </ToggleButton>
                            <ToggleButton value="ordens" sx={{ gap: 0.75, px: 2.25 }}>
                                <FileText size={14} strokeWidth={2} /> Ordens
                                <Box component="span" sx={{
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    minWidth: 18, height: 18, px: '5px', borderRadius: '9px', fontSize: '10px', fontWeight: 800,
                                    bgcolor: activeTab === 'ordens' ? 'info.bg' : 'background.default', color: activeTab === 'ordens' ? 'primary.main' : 'text.secondary',
                                }}>
                                    {orderServices.length}
                                </Box>
                            </ToggleButton>
                            <ToggleButton value="vendas" sx={{ gap: 0.75, px: 2.25 }}>
                                <ShoppingCart size={14} strokeWidth={2} /> Vendas
                                <Box component="span" sx={{
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    minWidth: 18, height: 18, px: '5px', borderRadius: '9px', fontSize: '10px', fontWeight: 800,
                                    bgcolor: activeTab === 'vendas' ? 'info.bg' : 'background.default', color: activeTab === 'vendas' ? 'primary.main' : 'text.secondary',
                                }}>
                                    {sales.length}
                                </Box>
                            </ToggleButton>
                        </ToggleButtonGroup>

                        {/* ABA: DADOS CADASTRAIS */}
                        {activeTab === 'cadastro' && (
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                                <FieldCard
                                    title="Dados cadastrais"
                                    editing={editingCards.cadastrais}
                                    canEdit={hasPermission('customer.update')}
                                    onEdit={() => handleEditSection('cadastrais')}
                                    onCancel={() => handleCancelSection('cadastrais')}
                                    onSave={handleSaveSection}
                                    saving={savingCustomer}
                                >
                                    {!editingCards.cadastrais ? (
                                        personType === 'pf' ? (
                                            <Box sx={roGridSx}>
                                                <RoField span label="Nome completo" value={displayName} />
                                                <RoField label="CPF" value={formatDocument(customer.cpf)} />
                                                <RoField
                                                    label="Data de nascimento"
                                                    value={customer.birthdate ? `${customer.birthdate}${customer.age ? ` (${customer.age} anos)` : ''}` : ''}
                                                />
                                            </Box>
                                        ) : (
                                            <Box sx={roGridSx}>
                                                <RoField span label="Razão social" value={customer.legal_name} />
                                                <RoField label="CNPJ" value={formatDocument(customer.cnpj)} />
                                                <RoField label="Nome fantasia" value={customer.trade_name} />
                                                <RoField label="Inscrição Estadual" value={customer.state_registration} />
                                                <RoField label="Inscrição Municipal" value={customer.municipal_registration} />
                                                <RoField label="Tipo de Atividade" value={customer.activity_type && customer.activity_type !== 'Selecione' ? customer.activity_type : ''} />
                                                <RoField span label="CNAE Principal" value={customer.principal_cnae} />
                                            </Box>
                                        )
                                    ) : (
                                        personType === 'pf' ? (
                                            <Box sx={editGridSx}>
                                                <TextField
                                                    label={requiredFieldLabel('Nome')}
                                                    fullWidth
                                                    name="first_name"
                                                    value={customerFormik.values.first_name}
                                                    onChange={customerFormik.handleChange}
                                                    error={customerFormik.touched.first_name && Boolean(customerFormik.errors.first_name)}
                                                    helperText={customerFormik.touched.first_name && customerFormik.errors.first_name}
                                                    variant="outlined"
                                                    sx={standaloneFieldSx}
                                                />
                                                <TextField
                                                    label={requiredFieldLabel('Sobrenome')}
                                                    fullWidth
                                                    name="last_name"
                                                    value={customerFormik.values.last_name}
                                                    onChange={customerFormik.handleChange}
                                                    error={customerFormik.touched.last_name && Boolean(customerFormik.errors.last_name)}
                                                    helperText={customerFormik.touched.last_name && customerFormik.errors.last_name}
                                                    variant="outlined"
                                                    sx={standaloneFieldSx}
                                                />
                                                <InputMasked
                                                    label="CPF"
                                                    fullWidth
                                                    name="cpf"
                                                    mask="999.999.999-99"
                                                    value={customerFormik.values.cpf}
                                                    onChange={customerFormik.handleChange}
                                                    error={customerFormik.touched.cpf && Boolean(customerFormik.errors.cpf)}
                                                    helperText={customerFormik.touched.cpf && customerFormik.errors.cpf}
                                                    variant="outlined"
                                                    sx={standaloneFieldSx}
                                                />
                                                <InputMasked
                                                    label="Data de nascimento"
                                                    fullWidth
                                                    name="birthdate"
                                                    mask="99/99/9999"
                                                    value={customerFormik.values.birthdate}
                                                    onChange={customerFormik.handleChange}
                                                    error={customerFormik.touched.birthdate && Boolean(customerFormik.errors.birthdate)}
                                                    helperText={customerFormik.touched.birthdate && customerFormik.errors.birthdate}
                                                    variant="outlined"
                                                    sx={standaloneFieldSx}
                                                />
                                            </Box>
                                        ) : (
                                            <Box sx={editGridSx}>
                                                <TextField
                                                    label={requiredFieldLabel('Nome Fantasia')}
                                                    fullWidth
                                                    name="trade_name"
                                                    value={customerFormik.values.trade_name}
                                                    onChange={customerFormik.handleChange}
                                                    error={customerFormik.touched.trade_name && Boolean(customerFormik.errors.trade_name)}
                                                    helperText={customerFormik.touched.trade_name && customerFormik.errors.trade_name}
                                                    variant="outlined"
                                                    sx={standaloneFieldSx}
                                                />
                                                <TextField
                                                    label={requiredFieldLabel('Razão Social')}
                                                    fullWidth
                                                    name="legal_name"
                                                    value={customerFormik.values.legal_name}
                                                    onChange={customerFormik.handleChange}
                                                    error={customerFormik.touched.legal_name && Boolean(customerFormik.errors.legal_name)}
                                                    helperText={customerFormik.touched.legal_name && customerFormik.errors.legal_name}
                                                    variant="outlined"
                                                    sx={standaloneFieldSx}
                                                />
                                                <TextField
                                                    label="CNPJ"
                                                    fullWidth
                                                    name="cnpj"
                                                    value={customerFormik.values.cnpj}
                                                    onChange={handleCnpjChange}
                                                    error={customerFormik.touched.cnpj && Boolean(customerFormik.errors.cnpj)}
                                                    helperText={customerFormik.touched.cnpj && customerFormik.errors.cnpj}
                                                    variant="outlined"
                                                    sx={standaloneFieldSx}
                                                />
                                                <Dropdown
                                                    label={requiredFieldLabel('Tipo de Atividade')}
                                                    labelVariant="static-micro"
                                                    data={ACTIVITY_TYPES}
                                                    selectedItem={customerFormik.values.activity_type}
                                                    handleChange={(value) => customerFormik.setFieldValue('activity_type', value)}
                                                    error={customerFormik.touched.activity_type && Boolean(customerFormik.errors.activity_type)}
                                                    helperText={customerFormik.touched.activity_type && customerFormik.errors.activity_type}
                                                    variant="outlined"
                                                    size="medium"
                                                    sx={standaloneFieldSx}
                                                />
                                                <TextField
                                                    label={requiredFieldLabel('Inscrição Estadual')}
                                                    fullWidth
                                                    name="state_registration"
                                                    value={customerFormik.values.state_registration}
                                                    onChange={customerFormik.handleChange}
                                                    error={customerFormik.touched.state_registration && Boolean(customerFormik.errors.state_registration)}
                                                    helperText={customerFormik.touched.state_registration && customerFormik.errors.state_registration}
                                                    variant="outlined"
                                                    sx={standaloneFieldSx}
                                                />
                                                <TextField
                                                    label={requiredFieldLabel('Inscrição Municipal')}
                                                    fullWidth
                                                    name="municipal_registration"
                                                    value={customerFormik.values.municipal_registration}
                                                    onChange={customerFormik.handleChange}
                                                    error={customerFormik.touched.municipal_registration && Boolean(customerFormik.errors.municipal_registration)}
                                                    helperText={customerFormik.touched.municipal_registration && customerFormik.errors.municipal_registration}
                                                    variant="outlined"
                                                    sx={standaloneFieldSx}
                                                />
                                                <Box sx={{ gridColumn: { sm: 'span 2' } }}>
                                                    <TextField
                                                        label="CNAE Principal"
                                                        fullWidth
                                                        name="principal_cnae"
                                                        value={customerFormik.values.principal_cnae}
                                                        onChange={customerFormik.handleChange}
                                                        error={customerFormik.touched.principal_cnae && Boolean(customerFormik.errors.principal_cnae)}
                                                        helperText={customerFormik.touched.principal_cnae && customerFormik.errors.principal_cnae}
                                                        variant="outlined"
                                                        sx={standaloneFieldSx}
                                                    />
                                                </Box>
                                            </Box>
                                        )
                                    )}
                                </FieldCard>

                                <FieldCard
                                    title="Contato"
                                    editing={editingCards.contato}
                                    canEdit={hasPermission('customer.update')}
                                    onEdit={() => handleEditSection('contato')}
                                    onCancel={() => handleCancelSection('contato')}
                                    onSave={handleSaveSection}
                                    saving={savingCustomer}
                                >
                                    {!editingCards.contato ? (
                                        <Box sx={roGridSx}>
                                            <RoField label="Telefone" value={formatPhone(customer.phone_number)} />
                                            <RoField label="Celular / WhatsApp" value={formatPhone(customer.cell_phone)} />
                                            <RoField span label="E-mail" value={customer.email} />
                                        </Box>
                                    ) : (
                                        <Box sx={editGridSx}>
                                            <InputMasked
                                                label="Telefone"
                                                fullWidth
                                                name="phone_number"
                                                mask="(99) 9999-9999"
                                                value={customerFormik.values.phone_number}
                                                onChange={customerFormik.handleChange}
                                                error={customerFormik.touched.phone_number && Boolean(customerFormik.errors.phone_number)}
                                                helperText={customerFormik.touched.phone_number && customerFormik.errors.phone_number}
                                                variant="outlined"
                                                sx={standaloneFieldSx}
                                            />
                                            <InputMasked
                                                label="Celular / WhatsApp"
                                                fullWidth
                                                name="cell_phone"
                                                mask="(99) 99999-9999"
                                                value={customerFormik.values.cell_phone}
                                                onChange={customerFormik.handleChange}
                                                error={customerFormik.touched.cell_phone && Boolean(customerFormik.errors.cell_phone)}
                                                helperText={customerFormik.touched.cell_phone && customerFormik.errors.cell_phone}
                                                variant="outlined"
                                                sx={standaloneFieldSx}
                                            />
                                            <Box sx={{ gridColumn: { sm: 'span 2' } }}>
                                                <TextField
                                                    label="E-mail"
                                                    fullWidth
                                                    name="email"
                                                    value={customerFormik.values.email}
                                                    onChange={customerFormik.handleChange}
                                                    error={customerFormik.touched.email && Boolean(customerFormik.errors.email)}
                                                    helperText={customerFormik.touched.email && customerFormik.errors.email}
                                                    variant="outlined"
                                                    sx={standaloneFieldSx}
                                                />
                                            </Box>
                                        </Box>
                                    )}
                                </FieldCard>

                                <FieldCard
                                    title="Endereço"
                                    span
                                    editing={editingCards.endereco}
                                    canEdit={hasPermission('customer.update')}
                                    onEdit={() => handleEditSection('endereco')}
                                    onCancel={() => handleCancelSection('endereco')}
                                    onSave={handleSaveSection}
                                    saving={savingCustomer}
                                >
                                    {!editingCards.endereco ? (
                                        <Box sx={roGridSx}>
                                            <RoField label="CEP" value={customer.cep} />
                                            <RoField label="Número" value={customer.number_address} />
                                            <RoField span label="Endereço" value={customer.address} />
                                            <RoField label="Complemento" value={customer.complement} />
                                            <RoField label="Bairro" value={customer.neighborhood} />
                                            <RoField span label="Cidade" value={customer.city} />
                                        </Box>
                                    ) : (
                                        <Box sx={editGridSx}>
                                            <InputMasked
                                                label="CEP"
                                                fullWidth
                                                name="cep"
                                                mask="99999-999"
                                                value={customerFormik.values.cep}
                                                onChange={customerFormik.handleChange}
                                                error={customerFormik.touched.cep && Boolean(customerFormik.errors.cep)}
                                                helperText={customerFormik.touched.cep && customerFormik.errors.cep}
                                                variant="outlined"
                                                sx={standaloneFieldSx}
                                            />
                                            <TextField
                                                label="Número"
                                                fullWidth
                                                name="number_address"
                                                value={customerFormik.values.number_address}
                                                onChange={customerFormik.handleChange}
                                                error={customerFormik.touched.number_address && Boolean(customerFormik.errors.number_address)}
                                                helperText={customerFormik.touched.number_address && customerFormik.errors.number_address}
                                                variant="outlined"
                                                sx={standaloneFieldSx}
                                            />
                                            <Box sx={{ gridColumn: { sm: 'span 2' } }}>
                                                <TextField
                                                    label="Endereço"
                                                    fullWidth
                                                    name="address"
                                                    value={customerFormik.values.address}
                                                    onChange={customerFormik.handleChange}
                                                    error={customerFormik.touched.address && Boolean(customerFormik.errors.address)}
                                                    helperText={customerFormik.touched.address && customerFormik.errors.address}
                                                    variant="outlined"
                                                    sx={standaloneFieldSx}
                                                />
                                            </Box>
                                            <TextField
                                                label="Complemento"
                                                fullWidth
                                                name="complement"
                                                value={customerFormik.values.complement}
                                                onChange={customerFormik.handleChange}
                                                error={customerFormik.touched.complement && Boolean(customerFormik.errors.complement)}
                                                helperText={customerFormik.touched.complement && customerFormik.errors.complement}
                                                variant="outlined"
                                                sx={standaloneFieldSx}
                                            />
                                            <TextField
                                                label="Bairro"
                                                fullWidth
                                                name="neighborhood"
                                                value={customerFormik.values.neighborhood}
                                                onChange={customerFormik.handleChange}
                                                error={customerFormik.touched.neighborhood && Boolean(customerFormik.errors.neighborhood)}
                                                helperText={customerFormik.touched.neighborhood && customerFormik.errors.neighborhood}
                                                variant="outlined"
                                                sx={standaloneFieldSx}
                                            />
                                            <Box sx={{ gridColumn: { sm: 'span 2' } }}>
                                                <TextField
                                                    label="Cidade"
                                                    fullWidth
                                                    name="city"
                                                    value={customerFormik.values.city}
                                                    onChange={customerFormik.handleChange}
                                                    error={customerFormik.touched.city && Boolean(customerFormik.errors.city)}
                                                    helperText={customerFormik.touched.city && customerFormik.errors.city}
                                                    variant="outlined"
                                                    sx={standaloneFieldSx}
                                                />
                                            </Box>
                                        </Box>
                                    )}
                                </FieldCard>
                            </Box>
                        )}

                        {/* ABA: VEÍCULOS */}
                        {activeTab === 'veiculos' && (
                            <Card elevation={0}>
                                <PanelHead
                                    title="Veículos vinculados"
                                    subtitle="Veículos cadastrados para este cliente"
                                    action={hasPermission('vehicle.create') && (
                                        <Button
                                            size="small"
                                            variant="contained"
                                            startIcon={<Plus size={14} strokeWidth={2} />}
                                            onClick={handleOpenVehicleModal}
                                        >
                                            Adicionar veículo
                                        </Button>
                                    )}
                                />
                                <Box sx={{ p: '14px 20px 20px' }}>
                                    {vehicles.length === 0 ? (
                                        <EmptyMiniState
                                            icon={Car}
                                            title="Nenhum veículo cadastrado"
                                            subtitle="Adicione o primeiro veículo deste cliente para vincular ordens de serviço a ele."
                                            actionLabel={hasPermission('vehicle.create') ? 'Adicionar veículo' : undefined}
                                            onAction={handleOpenVehicleModal}
                                        />
                                    ) : (
                                        <DefaultTable
                                            colunms={vehiclesColumns}
                                            data={vehicles}
                                            iconsActions={vehicleActions}
                                            loading={false}
                                            rowPerPage={8}
                                        />
                                    )}
                                </Box>
                            </Card>
                        )}

                        {/* ABA: ORDENS (HISTÓRICO) */}
                        {activeTab === 'ordens' && (
                            <Card elevation={0}>
                                <PanelHead
                                    title="Histórico de ordens de serviço"
                                    subtitle="Todas as ordens já geradas para este cliente"
                                />
                                <Box sx={{ p: '14px 20px 20px' }}>
                                    {orderServices.length === 0 ? (
                                        <EmptyMiniState
                                            icon={FileText}
                                            title="Nenhuma ordem de serviço ainda"
                                            subtitle="Quando uma ordem de serviço for gerada para este cliente, ela aparecerá aqui."
                                        />
                                    ) : (
                                        <DefaultTable
                                            colunms={orderServicesColumns}
                                            data={orderServices}
                                            iconsActions={orderServiceActions}
                                            loading={false}
                                            rowPerPage={8}
                                            onRowClick={(row) => handleViewOrderService(row.order_service_id)}
                                        />
                                    )}
                                </Box>
                            </Card>
                        )}

                        {/* ABA: VENDAS (HISTÓRICO) */}
                        {activeTab === 'vendas' && (
                            <Card elevation={0}>
                                <PanelHead
                                    title="Histórico de vendas"
                                    subtitle="Vendas realizadas no PDV identificadas com este cliente"
                                />
                                <Box sx={{ p: '14px 20px 20px' }}>
                                    {sales.length === 0 ? (
                                        <EmptyMiniState
                                            icon={ShoppingCart}
                                            title="Nenhuma venda ainda"
                                            subtitle="Quando uma venda for feita para este cliente no PDV, ela aparecerá aqui."
                                        />
                                    ) : (
                                        <DefaultTable
                                            colunms={salesColumns}
                                            data={sales}
                                            iconsActions={saleActions}
                                            loading={false}
                                            rowPerPage={8}
                                            onRowClick={(row) => handleViewSale(row.id)}
                                        />
                                    )}
                                </Box>
                            </Card>
                        )}
                    </>
                )}
            </Box>
            )}

            {/* MODAL: adicionar veículo (§5.6 center modal) */}
            <Dialog
                open={openVehicleModal}
                onClose={handleCloseVehicleModal}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden' } }}
            >
                <Box sx={{
                    px: '22px', pt: '20px', pb: '14px',
                    borderBottom: '1px solid', borderColor: 'divider',
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.25,
                }}>
                    <Box>
                        <Box sx={{
                            width: 38, height: 38, borderRadius: '11px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: 'info.bg', color: 'primary.main', mb: '10px',
                        }}>
                            <Car size={18} strokeWidth={2} />
                        </Box>
                        <Typography sx={{ fontSize: '17px', fontWeight: 800, mb: '4px' }}>
                            Adicionar veículo
                        </Typography>
                        <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
                            Vincule um veículo a {displayName || 'este cliente'}.
                        </Typography>
                    </Box>
                    <IconButton onClick={handleCloseVehicleModal} size="small">
                        <X size={15} strokeWidth={2} />
                    </IconButton>
                </Box>

                <Box component="form" onSubmit={vehicleFormik.handleSubmit} sx={{ p: '18px 22px', maxHeight: '64vh', overflowY: 'auto' }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
                        <Dropdown
                            label={requiredFieldLabel('Tipo de veículo')}
                            labelVariant="static-micro"
                            selectedItem={vehicleFormik.values.type}
                            handleChange={handleChangeType}
                            data={[
                                { id: 'car', name: 'CARRO' },
                                { id: 'motorcycle', name: 'MOTO' },
                                { id: 'bicycle', name: 'BICICLETA' },
                            ]}
                            error={vehicleFormik.touched.type && Boolean(vehicleFormik.errors.type)}
                            helperText={vehicleFormik.touched.type && vehicleFormik.errors.type}
                            variant="outlined"
                            size="medium"
                            sx={standaloneFieldSx}
                        />

                        {loadingManufacturers ? (
                            <Box>
                                <StaticMicroLabel>Fabricante</StaticMicroLabel>
                                <Skeleton variant="rounded" height={52} sx={{ borderRadius: '12px' }} />
                            </Box>
                        ) : (
                            <Dropdown
                                label={requiredFieldLabel('Fabricante')}
                                labelVariant="static-micro"
                                selectedItem={vehicleFormik.values.manufacturer_id}
                                handleChange={handleChangeManufacturer}
                                data={manufacturers || []}
                                disabled={!vehicleFormik.values.type}
                                error={vehicleFormik.touched.manufacturer_id && Boolean(vehicleFormik.errors.manufacturer_id)}
                                helperText={vehicleFormik.touched.manufacturer_id && vehicleFormik.errors.manufacturer_id}
                                variant="outlined"
                                size="medium"
                                sx={standaloneFieldSx}
                            />
                        )}

                        <Box sx={{ gridColumn: { sm: 'span 2' } }}>
                            <TextField
                                label={requiredFieldLabel('Modelo')}
                                fullWidth
                                name="model"
                                value={vehicleFormik.values.model}
                                onChange={vehicleFormik.handleChange}
                                disabled={!vehicleFormik.values.manufacturer_id}
                                error={vehicleFormik.touched.model && Boolean(vehicleFormik.errors.model)}
                                helperText={vehicleFormik.touched.model && vehicleFormik.errors.model}
                                variant="outlined"
                                sx={standaloneFieldSx}
                            />
                        </Box>

                        <TextField
                            label={requiredFieldLabel('Ano de fabricação')}
                            fullWidth
                            type="number"
                            name="fab_year"
                            value={vehicleFormik.values.fab_year}
                            onChange={vehicleFormik.handleChange}
                            error={vehicleFormik.touched.fab_year && Boolean(vehicleFormik.errors.fab_year)}
                            helperText={vehicleFormik.touched.fab_year && vehicleFormik.errors.fab_year}
                            variant="outlined"
                            inputProps={{ min: 1900, max: new Date().getFullYear() + 1 }}
                            sx={standaloneFieldSx}
                        />
                        <TextField
                            label={requiredFieldLabel('Ano do modelo')}
                            fullWidth
                            type="number"
                            name="mod_year"
                            value={vehicleFormik.values.mod_year}
                            onChange={vehicleFormik.handleChange}
                            error={vehicleFormik.touched.mod_year && Boolean(vehicleFormik.errors.mod_year)}
                            helperText={vehicleFormik.touched.mod_year && vehicleFormik.errors.mod_year}
                            variant="outlined"
                            inputProps={{ min: 1900, max: new Date().getFullYear() + 1 }}
                            sx={standaloneFieldSx}
                        />

                        {vehicleFormik.values.type !== 'bicycle' && (
                            <TextField
                                label={requiredFieldLabel('Placa')}
                                fullWidth
                                name="license_plate"
                                value={vehicleFormik.values.license_plate}
                                onChange={handleLicensePlateChange}
                                error={vehicleFormik.touched.license_plate && Boolean(vehicleFormik.errors.license_plate)}
                                helperText={vehicleFormik.touched.license_plate && vehicleFormik.errors.license_plate}
                                variant="outlined"
                                sx={{ ...standaloneFieldSx, '& .MuiInputBase-input': { ...standaloneFieldSx['& .MuiInputBase-input'], textTransform: 'uppercase', fontWeight: 700, letterSpacing: '.5px' } }}
                            />
                        )}
                        <TextField
                            label={requiredFieldLabel('Cor')}
                            fullWidth
                            name="color"
                            value={vehicleFormik.values.color}
                            onChange={vehicleFormik.handleChange}
                            error={vehicleFormik.touched.color && Boolean(vehicleFormik.errors.color)}
                            helperText={vehicleFormik.touched.color && vehicleFormik.errors.color}
                            variant="outlined"
                            sx={standaloneFieldSx}
                        />

                        <TextField
                            label="Km atual"
                            fullWidth
                            type="number"
                            name="kilometer"
                            value={vehicleFormik.values.kilometer}
                            onChange={vehicleFormik.handleChange}
                            error={vehicleFormik.touched.kilometer && Boolean(vehicleFormik.errors.kilometer)}
                            helperText={vehicleFormik.touched.kilometer && vehicleFormik.errors.kilometer}
                            variant="outlined"
                            inputProps={{ min: 0 }}
                            sx={standaloneFieldSx}
                        />
                        <TextField
                            label={requiredFieldLabel(vehicleFormik.values.type === 'bicycle' ? 'SN (Serial Number)' : 'Chassi')}
                            fullWidth
                            name="chassi"
                            value={vehicleFormik.values.chassi}
                            onChange={vehicleFormik.handleChange}
                            error={vehicleFormik.touched.chassi && Boolean(vehicleFormik.errors.chassi)}
                            helperText={vehicleFormik.touched.chassi && vehicleFormik.errors.chassi}
                            variant="outlined"
                            inputProps={{ maxLength: vehicleFormik.values.type === 'bicycle' ? undefined : 17 }}
                            sx={{ ...standaloneFieldSx, '& .MuiInputBase-input': { ...standaloneFieldSx['& .MuiInputBase-input'], textTransform: 'uppercase', fontFamily: 'monospace', letterSpacing: '.5px' } }}
                        />

                        <Box sx={{ gridColumn: { sm: 'span 2' } }}>
                            <TextAreaWithCounter
                                label="Observações (opcional)"
                                name="observation"
                                value={vehicleFormik.values.observation}
                                onChange={vehicleFormik.handleChange}
                                maxLength={500}
                                error={vehicleFormik.touched.observation && Boolean(vehicleFormik.errors.observation)}
                                helperText={vehicleFormik.touched.observation && vehicleFormik.errors.observation}
                                variant="outlined"
                                rows={3}
                                sx={standaloneFieldSx}
                            />
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ p: '16px 22px 20px', borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1.25 }}>
                    <Button variant="outlined" onClick={handleCloseVehicleModal}>Cancelar</Button>
                    <Button variant="contained" onClick={vehicleFormik.handleSubmit}>Salvar veículo</Button>
                </Box>
            </Dialog>

            {/* MODAL: excluir cliente (mobile) */}
            <DialogForm
                iconType="warning"
                open={openDeleteCustomerDialog}
                title="Atenção"
                confirmButtonText="Sim, Excluir"
                content="Tem certeza que deseja excluir o cliente?"
                secondaryContent="Esta ação não pode ser desfeita. O cliente será removido permanentemente do sistema."
                onSubmit={handleDeleteCustomer}
                onClose={() => setOpenDeleteCustomerDialog(false)}
            />

            {/* MODAL: remover veículo */}
            <DialogForm
                iconType="warning"
                open={openDeleteVehicleDialog}
                title="Remover veículo"
                confirmButtonText="Remover veículo"
                content={`Deseja realmente remover o veículo ${vehicleToDelete?.LicensePlate || ''} do cadastro do cliente?`}
                secondaryContent="Esta ação não pode ser desfeita."
                onSubmit={handleConfirmDeleteVehicle}
                onClose={handleCloseDeleteVehicle}
            />
        </PageScaffold>
    );
}
